import { useRef } from 'react';
import type { Snapshot } from '../../stores/controle-store';

interface SnapshotHeaderProps {
  snapshot: Snapshot | null;
  isCreating: boolean;
  isSaving: boolean;
  onFetch: () => void;
  onRefresh: () => void;
  onApprove: () => void;
  onBankImport?: (files: File[]) => void;
  onBankAutoImport?: () => void;
  isBankLoading?: boolean;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function SnapshotHeader({ snapshot, isCreating, isSaving, onFetch, onRefresh, onApprove, onBankImport, onBankAutoImport, isBankLoading }: SnapshotHeaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // No snapshot — show fetch prompt
  if (!snapshot) {
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-3 text-sm text-blue-800">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Maand nog niet opgehaald. Klik op ophalen om SF, Mollie en MyPos data te laden.</span>
        </div>
        <button
          onClick={onFetch}
          disabled={isCreating}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {isCreating && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          {isCreating ? 'Bezig...' : 'Ophalen'}
        </button>
      </div>
    );
  }

  // Approved — green banner
  if (snapshot.status === 'goedgekeurd') {
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="flex items-center gap-3 text-sm text-emerald-800">
          <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            Goedgekeurd op {formatDateTime(snapshot.approved_at)}
            {snapshot.approved_by && <> door <strong>{snapshot.approved_by}</strong></>}
          </span>
        </div>
        <span className="text-xs text-emerald-600">
          Opgehaald op {formatDateTime(snapshot.created_at)}
        </span>
      </div>
    );
  }

  // Open snapshot — gray banner with actions
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-3 text-sm text-gray-700">
        <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          Opgehaald op {formatDateTime(snapshot.created_at)}
          {snapshot.created_by && <> door <strong>{snapshot.created_by}</strong></>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onRefresh}
          disabled={isCreating}
          className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
        >
          {isCreating && <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />}
          {isCreating ? 'Bezig...' : 'Opnieuw ophalen'}
        </button>
        {onBankAutoImport && (
          <button
            onClick={onBankAutoImport}
            disabled={isBankLoading}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isBankLoading ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            )}
            {isBankLoading ? 'Bank ophalen...' : 'Bank ophalen'}
          </button>
        )}
        {onBankImport && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 flex items-center gap-2"
              title="Fallback: CAMT.053-export handmatig uploaden"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Bank XML
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) onBankImport(Array.from(files));
                e.target.value = '';
              }}
            />
          </>
        )}
        <button
          onClick={onApprove}
          disabled={isSaving}
          className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
        >
          {isSaving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Goedkeuren
        </button>
      </div>
    </div>
  );
}
